// Marcas y modelos estándar (fase 3 de "marcas y modelos sin catálogo").
//
// Con el switch "Estandarizar marcas y modelos" (varinicial.normalizamarcas)
// el backend guarda la marca/modelo con la escritura estándar del
// diccionario compartido. Este hook lo hace visible antes de guardar:
//   - opciones para autocompletar (marcas estándar y sus modelos), y
//   - resolver(pares): cómo quedaría cada {marca, modelo}, sin guardar nada.
// Con el switch apagado `activo` es false, no hay opciones y resolver
// devuelve lo digitado: la pantalla se comporta como siempre.
//
// La lógica de normalización vive SOLO en el backend (app/marcasmodelos.py);
// aquí no se replica. `claveMarca` es solo para emparejar la marca escrita
// con la lista de opciones.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMaterialUIController } from "context";
import { useApi } from "lib/api";

// Recién desplegado, el backend puede tardar unos segundos en tener el
// diccionario en memoria: se reintenta una vez.
const REINTENTO_MS = 4000;

let cache = null; // {usuario, data}: no se repide al cambiar de pantalla

export const claveMarca = (s) =>
  String(s || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Z0-9]/g, "");

export function useMarcasEstandar() {
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const usuario = controller.usuarioLog;
  const propio = cache && cache.usuario === usuario ? cache.data : null;
  const [data, setData] = useState(propio || { activo: false, marcas: [] });
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    if (propio && !propio.cargando) return undefined;
    let timer;
    const pedir = (reintentar) =>
      post("/api/marcasmodelos_opciones", {}).then(({ ok, data: d }) => {
        if (!ok || !vivo.current) return;
        cache = { usuario, data: d };
        setData(d);
        if (d.cargando && reintentar) timer = setTimeout(() => pedir(false), REINTENTO_MS);
      });
    pedir(true);
    return () => {
      vivo.current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const porClave = useMemo(() => {
    const m = new Map();
    (data.marcas || []).forEach((x) => m.set(claveMarca(x.marca), x));
    return m;
  }, [data]);

  const marcas = useMemo(() => (data.marcas || []).map((x) => x.marca), [data]);

  // Modelos estándar de la marca escrita (sin importar espacios/guiones).
  const modelosDe = useCallback(
    (marca) => porClave.get(claveMarca(marca))?.modelos || [],
    [porClave]
  );

  // [{marca, modelo}] -> [{marca, modelo, cambio, conocido}] en el mismo orden.
  const resolver = useCallback(
    async (pares) => {
      if (!data.activo || !pares.length) {
        return pares.map((p) => ({
          marca: p.marca,
          modelo: p.modelo,
          cambio: false,
          conocido: "",
        }));
      }
      const { ok, data: d } = await post("/api/marcasmodelos_resolver", { pares });
      if (!ok)
        return pares.map((p) => ({
          marca: p.marca,
          modelo: p.modelo,
          cambio: false,
          conocido: "",
        }));
      return d.resultados || [];
    },
    [data.activo, post]
  );

  return { activo: !!data.activo, marcas, modelosDe, resolver };
}

// Une la lista estándar con la propia de la pantalla, sin duplicar por clave
// y con la escritura estándar primero.
export function unirOpciones(estandar, propias) {
  const vistas = new Set();
  const salida = [];
  [...estandar, ...propias].forEach((v) => {
    const k = claveMarca(v);
    if (!v || vistas.has(k)) return;
    vistas.add(k);
    salida.push(v);
  });
  return salida.sort();
}

// Para un solo par, con espera mientras se escribe: el resultado del backend
// cuando se va a guardar distinto ("Se guardará como ...") o cuando hay una
// escritura estándar parecida que ofrecer ("¿Quisiste decir ...?"); null si
// no hay nada que mostrar.
export function useVistaPrevia(resolver, activo, marca, modelo, esperaMs = 450) {
  const [vista, setVista] = useState(null);
  useEffect(() => {
    if (!activo || !String(marca || "").trim()) {
      setVista(null);
      return undefined;
    }
    let vigente = true;
    const t = setTimeout(() => {
      resolver([{ marca, modelo }]).then(([r]) => {
        if (vigente) setVista(r && (r.cambio || r.sugerencia) ? r : null);
      });
    }, esperaMs);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [activo, marca, modelo, resolver, esperaMs]);
  return vista;
}
